import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useDisclosure, Button, Text, Link } from "@chakra-ui/react";
import TableStructure from "../../CommonComponents/TableStructure";
import { getExportList } from "../../services/Export";
import LogModal from "../DeriveFile/LogModal";
import { common } from "../../constants/constants";
import dayjs from "dayjs";

const ExportListTable = () => {
  const [records, setRecords] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchText, setSearchText] = useState('');
  const isInitialLoad = useRef(true);
  const [sortOrder, setSortOrder] = useState("asc");
  const session = useSelector((state) => state.session);
  const limit = 50; // Number of records per page
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedRecordId, setSelectedRecordId] = useState(null);

  // Fetch export list from the API
  const fetchExportList = async (page, term) => {
    setLoading(true);
    try {
      const result = await getExportList(limit, page, term);
      if (result?.data) {
        const newRecords = result?.data?.data || [];
        setRecords(newRecords);
        setTotalPages(Math.ceil(result?.data?.total / limit));
        setTotalRecords(result?.data?.total);
        if (result?.data?.page_number && parseInt(result?.data?.page_number) !== currentPage) {
          setCurrentPage(parseInt(result?.data?.page_number));
        }
        if (isInitialLoad.current) {
          isInitialLoad.current = false;
        }
        // Return records for use in handleRefresh
        return newRecords;
      }
    } catch (error) {
      console.error("Error fetching export list:", error);
    } finally {
      setLoading(false);
    }
    return [];
  };

  const handleViewLog = (log, status, recordId) => {
    // Ensure log is an array (LogModal expects an array)
    const logData = Array.isArray(log) ? log : (log ? [log] : []);
    setSelectedLog(logData);
    setSelectedStatus(status);
    setSelectedRecordId(recordId);
    onOpen();
  };

  const handleRefresh = async () => {
    // Refresh the export list and get updated records
    const updatedRecords = await fetchExportList(currentPage, "");
    // After refresh, update the selected log if we have a record ID
    if (selectedRecordId && updatedRecords && updatedRecords?.length > 0) {
      const updatedRecord = updatedRecords?.find(r => r.id === selectedRecordId || r._id === selectedRecordId);
      if (updatedRecord && updatedRecord?.log) {
        const logData = Array.isArray(updatedRecord?.log) ? updatedRecord?.log : (updatedRecord?.log ? [updatedRecord?.log] : []);
        setSelectedLog(logData);
        setSelectedStatus(updatedRecord?.status);
      }
    }
  };

  const columns = [
    {
      label: "Module",
      accessor: "module_name",
    },
    {
      label: "Params",
      accessor: "module_inputs",
      render: (value) => {
        if (!value) return <Text color="gray.400" fontStyle="italic">—</Text>;
        // If it's an object, stringify it; otherwise display as is
        if (typeof value === 'object') {
          const stringified = JSON.stringify(value);
          // If empty object, show dash
          if (stringified === '{}') {
            return <Text color="gray.400" fontStyle="italic">—</Text>;
          }
          return <Text fontSize="sm" title={stringified}>{stringified}</Text>;
        }
        return <Text fontSize="sm">{value}</Text>;
      }
    },
    {
      label: "Created At",
      accessor: "created_at",
      render: (value) => {
        return value
          ? dayjs(value).format("DD MMM YYYY, hh:mm A")
          : "--";
      }
    },
    {
      label: "Created By",
      accessor: "created_by",
      render: (value) => {
        // If created_by is an object with username, extract it
        if (value && typeof value === 'object' && value.username) {
          return value.username;
        }
        // If it's already a string, return as is
        return value || 'N/A';
      }
    },
    {
      label: "Download",
      accessor: "export_url",
      render: (value, record) => {
        // Check multiple possible field names for download URL (similar to derive-file)
        const downloadUrl = value || record?.export_url || record?.s3_url || record?.file_url || record?.download_url || record?.derived_s3_url;
        return downloadUrl ? (
          <Link href={downloadUrl} isExternal color="blue.500" onClick={(e) => e.stopPropagation()}>
            {common.DOWNLOAD || "Download"}
          </Link>
        ) : (
          <Text color="gray.400" fontStyle="italic">
            {common.NOT_AVAILABLE || "Not Available"}
          </Text>
        );
      }
    },
    {
      label: "Status",
      accessor: "status",
      render: (value) => {
        return value || "--";
      }
    },
    {
      label: "Log",
      accessor: "log",
      render: (value, record) => {
        const logData = record?.log || value || [];
        const status = record?.status;
        const recordId = record?.id || record?._id;
        // Only show button if log data exists
        if (!Array.isArray(logData) || logData.length === 0) {
          return <Text color="gray.400" fontStyle="italic">—</Text>;
        }
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleViewLog(logData, status, recordId);
            }}
          >
            {common.VIEW || "View"}
          </Button>
        );
      }
    }
  ];

  // Handle search button click
  const handleSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      isInitialLoad.current = false;
      setCurrentPage(1);
      setSearchText(searchTerm);
      fetchExportList(1, searchTerm);
    }
  };

  const sortRecords = () => {
    const newSortOrder = sortOrder === "asc" ? "desc" : "asc";
    setSortOrder(newSortOrder);

    const sorted = [...records].sort((a, b) =>
      newSortOrder === "asc"
        ? (a.module_name || '').localeCompare(b.module_name || '')
        : (b.module_name || '').localeCompare(a.module_name || '')
    );

    setRecords(sorted);
  };

  useEffect(() => {
    fetchExportList(currentPage, searchText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleClear = () => {
    setSearchText('');
    fetchExportList(1, "");
  };

  // Handle page changes
  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(parseInt(page));
    }
  };

  return (
    <>
      <TableStructure
        isAuthenticated={session?.isAuthenticated}
        onRefresh={handleRefresh}
        handleSearch={handleSearch}
        handleClear={handleClear}
        handlePageChange={handlePageChange}
        currentPage={currentPage}
        totalRecords={totalRecords}
        totalPages={totalPages}
        sortRecords={sortRecords}
        sortOrder={sortOrder}
        loading={loading}
        records={records}
        type="export"
        title="Export List"
        columns={columns}
        hideActions={true}
        disableRowClick={true}
      />
      <LogModal
        isOpen={isOpen}
        onClose={onClose}
        logData={selectedLog}
        onRefresh={handleRefresh}
        status={selectedStatus}
      />
    </>
  );
};

export default ExportListTable;
